import React, {
    useEffect, useContext, useState, useCallback, useMemo
} from 'react';
import _ from 'lodash';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import { TranslatorContext } from '@jutro/locale';
import { useValidation } from 'gw-portals-validation-react';
import { SgVehicleLookupUtil } from 'saga-modules';
import { SgVehicleLookupService } from 'saga-capabilities';
import SgSelect from 'sg-select';
import SpmConstants from 'sg-spm-constants';
import appConfig from 'app-config';
import { publish } from '@jutro/events';
import ADOBE_TOPICS from 'sg-adobe-analytics/AdobeTopics';
import metadata from './SgManualVehicleLookup.metadata.json5';
import messages from './SgManualVehicleLookup.messages';

function SpmManualVehicleLookup(props) {
    const translator = useContext(TranslatorContext);
    const {
        id,
        modelVM,
        vehicleModel,
        updateData,
        changeVehicleView,
        showErrors,
        onValidate: onValidateParent,
        state,
        availableVehicles,
        isCarInsurance,
        handleVehicleValue
    } = props;
    const {
        isComponentValid,
        onValidate
    } = useValidation(id);

    const { isQuickQuote } = appConfig;
    const vehicleModelPaths = useMemo(() => {
        return ['model', 'year', 'numberOfDoors', 'transmission', 'engineCapacity',
            'description', 'hasExperianValue', 'vehicleValue', 'isTrackerInstalled', 'hasModifications', 'yearPurchased'];
    }, []);

    useEffect(() => {
        if (onValidateParent) {
            onValidateParent(isComponentValid, id);
        }
        return () => {
            if (onValidateParent) {
                // workaround when component was not valid but is no longer needed (hidden)
                onValidateParent(true, id);
            }
        };
    }, [id, isComponentValid, onValidateParent]);

    const [isInitialEditRender, setIsInitialEditRender] = useState(state === 'edit');
    const [dropdownsToUpdate, setDropdownsToUpdate] = useState([]);

    const [listOfModels, updateModels] = useState(availableVehicles.models);
    const [listOfYears, updateYears] = useState(availableVehicles.years);
    const [listOfDoors, updateDoors] = useState([]);
    const [listOfTransmissions, updateTransmissions] = useState([]);
    const [listOfEngineCapacities, updateEngineCapacities] = useState(availableVehicles.engineCapacities);
    const [listOfDescriptions, updateDescriptions] = useState([]);
    const [abiCars, updateAbiCars] = useState(availableVehicles.abiCars);
    const [abiFilteredCars, updateAbiFilteredCars] = useState(availableVehicles.abiCars);

    const toRegLookup = useCallback(() => {
        changeVehicleView(SpmConstants.VEHICLE_LOOKUP_VIEWS.REGISTRATION_LOOKUP);
        updateData(modelVM);
    }, [changeVehicleView]);

    const updateABICarsList = useCallback(() => {
        if (!_.isEmpty(abiCars)) {
            const filteredAbiCars = SgVehicleLookupUtil.updateFilteredAbiCars(abiCars, vehicleModel);
            updateAbiFilteredCars(filteredAbiCars);
            return filteredAbiCars;
        }
        return undefined;
    }, [abiCars, vehicleModel]);

    const clearVehicleProperties = useCallback((propertiesList) => {
        // Does not apply to Motorhomes
        if (isCarInsurance && !isInitialEditRender) {
            SgVehicleLookupUtil.clearVehicleProperties(vehicleModel, propertiesList);
        }
    }, [isCarInsurance, isInitialEditRender, vehicleModel]);

    const updateDependentFields = useCallback((fieldPath) => {
        const currentIndex = _.indexOf(vehicleModelPaths, fieldPath);
        let vehicleModelPathsToUpdate = vehicleModelPaths.slice(currentIndex + 1, vehicleModelPaths.length);
        vehicleModelPathsToUpdate = isQuickQuote ? vehicleModelPathsToUpdate.filter((path) => path !== 'hasModifications')
            : vehicleModelPathsToUpdate;
        clearVehicleProperties(vehicleModelPathsToUpdate);
        setDropdownsToUpdate(vehicleModelPathsToUpdate);
    }, [clearVehicleProperties, vehicleModelPaths]);

    const updateManualVehicleData = useCallback((value, fieldPath) => {
        _.set(vehicleModel, fieldPath, value);
        updateDependentFields(fieldPath);
        updateData(modelVM);
        updateABICarsList();
    }, [modelVM, updateABICarsList, updateDependentFields, updateData, vehicleModel]);

    const updateAvailableValues = useCallback((availableVehicleValues, initialRenderDropdownsToUpdate = []) => {
        if (dropdownsToUpdate.includes('numberOfDoors') || initialRenderDropdownsToUpdate.includes('numberOfDoors')) {
            updateDoors(availableVehicleValues.doors);
        }
        if (dropdownsToUpdate.includes('transmission') || initialRenderDropdownsToUpdate.includes('transmission')) {
            updateTransmissions(availableVehicleValues.transmissions);
        }
        if (dropdownsToUpdate.includes('engineCapacity') || initialRenderDropdownsToUpdate.includes('engineCapacity')) {
            updateEngineCapacities(availableVehicleValues.engineCapacities);
        }
        if (dropdownsToUpdate.includes('description') || initialRenderDropdownsToUpdate.includes('description')) {
            updateDescriptions(availableVehicleValues.descriptions);
        }
    }, [dropdownsToUpdate]);

    const prepopulateValues = useCallback((availableVehicleValues) => {
        if (availableVehicleValues.doors.length === 1) {
            vehicleModel.numberOfDoors.value = availableVehicleValues.doors[0].name;
        }
        if (availableVehicleValues.transmissions.length === 1) {
            vehicleModel.transmission.value = availableVehicleValues.transmissions[0].code;
        }
        if (availableVehicleValues.engineCapacities.length === 1) {
            vehicleModel.engineCapacity.value = availableVehicleValues.engineCapacities[0].name;
        }
        if (availableVehicleValues.descriptions.length === 1) {
            vehicleModel.description.value = availableVehicleValues.descriptions[0].name;
        }
    }, [vehicleModel]);

    const getTransmissionName = useCallback((transmissionCode) => {
        if (transmissionCode === 'MAN') {
            return 'MANUAL';
        }
        return 'AUTOMATIC';
    }, []);

    const getAvailableVehicles = useCallback((filteredAbiCars) => {
        const availableVehicleValues = {
            doors: [],
            transmissions: [],
            engineCapacities: [],
            descriptions: []
        };
        _.each(filteredAbiCars, (abiCar) => { // Plucking the values this way the array is only iterated once
            availableVehicleValues.doors.push({
                code: abiCar.numberOfDoors,
                name: abiCar.numberOfDoors
            });
            availableVehicleValues.transmissions.push({
                code: abiCar.transmission,
                name: getTransmissionName(abiCar.transmission)
            });
            availableVehicleValues.engineCapacities.push({
                code: abiCar.engineCapacity,
                name: abiCar.engineCapacity
            });
            availableVehicleValues.descriptions.push({
                code: abiCar.description,
                name: abiCar.description
            });
        });
        availableVehicleValues.doors = _.sortBy(_.uniqBy(availableVehicleValues.doors, 'code'), 'code');
        availableVehicleValues.transmissions = _.orderBy(_.uniqBy(availableVehicleValues.transmissions, 'name'), 'code', 'desc');
        availableVehicleValues.engineCapacities = _.sortBy(_.uniqBy(availableVehicleValues.engineCapacities, 'code'), 'code');
        availableVehicleValues.descriptions = _.sortBy(_.uniqBy(availableVehicleValues.descriptions, 'name'), 'code');
        return availableVehicleValues;
    }, [abiFilteredCars, getTransmissionName]);

    useEffect(() => {
        if (abiFilteredCars.length > 0) {
            const availableVehicleValues = getAvailableVehicles(abiFilteredCars);
            updateAvailableValues(availableVehicleValues);
            prepopulateValues(availableVehicleValues);
        } else {
            updateTransmissions([
                { code: 'MAN', name: getTransmissionName('MAN') },
                { code: 'AUTO', name: getTransmissionName('AUTO') }
            ]);
        }
    }, [abiFilteredCars]);

    useEffect(() => {
        if (vehicleModel.make.value !== undefined && !isInitialEditRender && isCarInsurance) {
            SgVehicleLookupService.lookupModels(vehicleModel.make.value)
                .then((vehicleModels) => {
                    if (vehicleModels.length === 1 && vehicleModel.make.value !== undefined) {
                        updateManualVehicleData(vehicleModels[0].name, 'model');
                    }
                    updateModels(_.sortBy(vehicleModels, 'name'));
                });
        }
    }, [vehicleModel.make.value]);

    useEffect(() => {
        if (vehicleModel.model.value !== undefined && !isInitialEditRender && isCarInsurance) {
            SgVehicleLookupService.lookupYears(vehicleModel.make.value, vehicleModel.model.value)
                .then((vehicleYears) => {
                    if (vehicleYears.length === 1 && vehicleModel.model.value !== undefined) {
                        updateManualVehicleData(vehicleYears[0].name, 'years');
                    }
                    updateYears(vehicleYears);
                });
        }
    }, [vehicleModel.model.value]);

    useEffect(() => {
        if (vehicleModel.year.value !== undefined && !isInitialEditRender && isCarInsurance) {
            updateAbiCars([]);
            SgVehicleLookupService.autofillBasedOnPartialDto({
                make: vehicleModel.make.value,
                model: vehicleModel.model.value,
                year: vehicleModel.year.value
            }).then((result) => {
                updateAbiCars(result.abiCars);
                updateAbiFilteredCars(result.abiCars);
            });
        }
    }, [vehicleModel.year.value]);

    useEffect(() => {
        if (vehicleModel.description.value !== undefined && !isInitialEditRender && isCarInsurance) {
            let selectedVehicle;
            const vehicleMatches = _.filter(abiFilteredCars, {
                description: vehicleModel.description.value,
                make: vehicleModel.make.value,
                model: vehicleModel.model.value,
                engineCapacity: vehicleModel.engineCapacity.value,
                transmission: vehicleModel.transmission.value
            });
            if (vehicleMatches.length > 1) {
                // if more than one match check for a match of the registration year
                let yearMatch = _.filter(vehicleMatches, (vehicle) => {
                    return vehicle.firstYear <= vehicleModel.year.value && vehicle.lastYear >= vehicleModel.year.value;
                });
                if (yearMatch.length > 1) { // if no registration year match found, find the cars built before the registration year
                    yearMatch = _.filter(vehicleMatches, (vehicle) => {
                        return vehicle.firstYear < vehicleModel.year.value && vehicle.lastYear < vehicleModel.year.value;
                    });
                }
                if (_.isEmpty(yearMatch)) {
                    [selectedVehicle] = vehicleMatches; // fallback to first found vehicle if no matches to year
                } else {
                    selectedVehicle = yearMatch[yearMatch.length - 1]; // select the latest car closest to registration year
                }
            } else {
                [selectedVehicle] = vehicleMatches;
            }
            vehicleModel.value = _.merge(
                { ...vehicleModel.value },
                selectedVehicle,
            );
            handleVehicleValue(selectedVehicle);
            updateData(_.clone(modelVM));
            changeVehicleView(SpmConstants.VEHICLE_LOOKUP_VIEWS.VEHICLE_SUMMARY);
        }
        publish(ADOBE_TOPICS.VEHICLE_LOOKUP_TRACKING, { vehicleModel });
    }, [vehicleModel.description.value]);

    useEffect(() => {
        if (!isCarInsurance && vehicleModel.motorhomeEngineCapacity.value) {
            vehicleModel.description.value = `${SpmConstants.WEB_MOTOR_HOME_DESCRIPTION_FIELD}${vehicleModel.motorhomeEngineCapacity.value.code}CC`;
        }
    }, [vehicleModel.motorhomeEngineCapacity.value]);

    useEffect(() => {
        if (isInitialEditRender) {
            // create correct dropdown list values based on current vehicle selection
            const currentVehicleCopy = _.clone(vehicleModel.value);
            const vehicleModelPathsToUpdate = vehicleModelPaths.slice(2, 6); // 'numberOfDoors', 'transmission', 'engineCapacity', 'description'
            _.each(vehicleModelPathsToUpdate, (vehicleItem) => {
                vehicleModel[vehicleItem].value = undefined; // remove values to update complete applicable dropdown lists
            });
            _.each(vehicleModelPathsToUpdate, (vehicleItem, index) => {
                const filteredVehicles = updateABICarsList(); // filter full list of vehicles
                const filteredAvailableVehicles = getAvailableVehicles(filteredVehicles); // filter available vehicles for dropdown lists
                updateAvailableValues(filteredAvailableVehicles, vehicleModelPathsToUpdate.slice(index, vehicleModelPathsToUpdate.length)); // update dropdown lists based on filtered vehicle
                vehicleModel[vehicleItem].value = currentVehicleCopy[vehicleItem]; // reset vehicle item value back to original
            });
            vehicleModel.value = currentVehicleCopy; // reset any other values
            setIsInitialEditRender(false);
        }
    }, []);

    const overrideProps = {
        blankRegText: {
            visible: vehicleModel.registrationNumber.value === undefined ? '' : false
        },
        make: {
            visible: isCarInsurance,
            path: 'make',
            values: [...availableVehicles.makes],
            onValidate: onValidate,
            value: _.get(vehicleModel, 'make.value'),
            validationMessages: [
                _.get(vehicleModel, 'make.aspects.validationMessage')
            ],
            onModelChange: updateManualVehicleData,
            modelVM: vehicleModel,
            blockOnBlur: true
        },
        model: {
            disabled: !vehicleModel.make.value,
            availableValues: [...listOfModels],
            visible: isCarInsurance
        },
        yearOfRegistration: {
            disabled: !vehicleModel.model.value,
            availableValues: [...listOfYears]
        },
        numberOfDoors: {
            disabled: !vehicleModel.year.value,
            availableValues: [...listOfDoors],
            visible: isCarInsurance
        },
        transmission: {
            disabled: !vehicleModel.numberOfDoors.value,
            availableValues: [...listOfTransmissions],
            visible: isCarInsurance
        },
        engineCapacity: {
            disabled: !vehicleModel.transmission.value && isCarInsurance,
            availableValues: [...listOfEngineCapacities],
            path: isCarInsurance ? 'engineCapacity' : 'motorhomeEngineCapacity'
        },
        description: {
            disabled: _.isNil(vehicleModel.engineCapacity.value),
            availableValues: [...listOfDescriptions],
            visible: isCarInsurance,
            validationMessages: vehicleModel.description.value === undefined ? [translator(messages.descriptionValidation)] : null
        },
    };

    const resolvers = {
        resolveCallbackMap: {
            toRegLookup,
        },
        resolveComponentMap: {
            SgSelect
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={vehicleModel}
            onValueChange={updateManualVehicleData}
            overrideProps={overrideProps}
            showErrors={showErrors}
            onValidationChange={onValidate}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
        />
    );
}

export default SpmManualVehicleLookup;
